import { Handshake } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ScopePicker } from "@/components/layout/ScopePicker";
import { useDealCaptureLookups } from "@/hooks/useDealCapture";
import {
  areaListPath,
  areaPath,
  dealPath,
  keepFlow,
  locate,
} from "@/lib/deal-capture/paths";
import { dealsInFlow, flowInScope } from "@/lib/deal-capture/flow";
import { formatNumber } from "@/lib/format";
import type { SidebarSlotProps } from "@/lib/module-registry";

import { ALL_ID, MATCHES_ID, matches, pickerOptions } from "./picker-options";

/**
 * The Deal picker, under the Area picker while the area is Deals. Searches a
 * deal's description, number, buyer and seller; lists the top 50 matches in
 * the flow in scope (Integrated or Non-Integrated), newest deal first. Choosing one opens it; "All deals" opens the list.
 */
export function DealPicker({ onClose }: SidebarSlotProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useDealCaptureLookups();
  const [query, setQuery] = useState("");
  const { recordId, page } = locate(pathname);
  const allDeals = useMemo(() => data?.deals ?? [], [data]);
  const openDeal = allDeals.find((d) => d.number === recordId);
  // Only the flow in scope: an open deal's own, else the toggle's.
  const flow = flowInScope(search, openDeal);
  const deals = useMemo(() => dealsInFlow(allDeals, flow), [allDeals, flow]);
  const listQuery = new URLSearchParams(search).get("q") ?? "";

  const matched = useMemo(
    () =>
      deals
        .filter((d) => matches(query, d.description, d.number, d.buyer, d.seller))
        .sort((a, b) => Number(b.number) - Number(a.number))
        .map((d) => ({
          id: d.number,
          label: d.description,
          hint: `#${d.number} · ${d.buyer} · ${d.seller}`,
        })),
    [deals, query],
  );
  const { options, footer } = pickerOptions(query, matched, deals.length, "All deals");

  const listMatches = deals.filter((d) =>
    matches(listQuery, d.description, d.number, d.buyer, d.seller),
  ).length;
  const selected = openDeal
    ? { id: openDeal.number, label: `${openDeal.number} · ${openDeal.description}` }
    : page === "list"
      ? listQuery
        ? {
            id: MATCHES_ID,
            label: `“${listQuery}” · ${formatNumber(listMatches)} ${listMatches === 1 ? "match" : "matches"}`,
          }
        : { id: ALL_ID, label: `All deals · ${formatNumber(deals.length)}` }
      : null;

  const listWith = (q: string) =>
    `${areaListPath("deals")}${q ? `?${new URLSearchParams({ q }).toString()}` : ""}`;

  return (
    <ScopePicker
      axis="Deal"
      icon={Handshake}
      selected={selected}
      placeholder="Find a deal"
      searchPlaceholder={`Search ${formatNumber(deals.length)} deals by name or #`}
      emptyMessage="No deals match that name."
      options={options}
      isLoading={isLoading}
      footer={footer}
      onSearch={setQuery}
      onSelect={(id) => {
        if (id === ALL_ID) navigate(keepFlow(listWith(""), search));
        else if (id === MATCHES_ID) navigate(keepFlow(listWith(query.trim()), search));
        else navigate(keepFlow(dealPath(id), search));
        onClose();
      }}
      onClear={() => {
        navigate(keepFlow(areaPath("deals"), search));
        onClose();
      }}
    />
  );
}
